import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { fetchBlog } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useSEO, breadcrumbJsonLd, siteUrl } from "@/lib/seo";

const Blog = () => {
  const { lang } = useLang();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetchBlog().then(setPosts).catch(() => {});
  }, []);

  useSEO({
    title: lang === "nl"
      ? "Kennisbank — Truckwash, HACCP, NVWA & Fleet Cleaning | Wasz.nl"
      : "Knowledge Base — Truckwash, HACCP, NVWA & Fleet Cleaning | Wasz.nl",
    description: lang === "nl"
      ? "Praktische artikelen over mobiele truckwash, HACCP-vrachtwagenreiniging, NVWA-controles, Fleet Cleaning-contracten en duurzaam wassen."
      : "Practical articles on mobile truckwash, HACCP truck cleaning, NVWA inspections, fleet cleaning contracts and sustainable washing.",
    canonical: siteUrl("/blog"),
    lang,
    jsonLd: [
      {
        id: "blog-breadcrumb",
        data: breadcrumbJsonLd([
          { name: "Home", url: siteUrl("/") },
          { name: lang === "nl" ? "Kennisbank" : "Knowledge Base", url: siteUrl("/blog") },
        ]),
      },
    ],
  });

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <main data-testid="blog-page" className="min-h-screen pt-20 pb-24">
      <section className="mx-auto max-w-7xl px-6 lg:px-8 mb-12">
        <p className="section-overline mb-3">{lang === "nl" ? "Kennisbank" : "Knowledge Base"}</p>
        <h1 className="text-4xl sm:text-6xl font-black tracking-tighter text-white leading-[1.05] max-w-3xl">
          {lang === "nl" ? "Inzichten voor transport-Nederland." : "Insights for transport Netherlands."}
        </h1>
        <p className="mt-5 text-white/60 text-lg max-w-2xl leading-relaxed">
          {lang === "nl"
            ? "Praktische artikelen over mobiele truckwash, compliance, Fleet Cleaning en duurzaamheid — door en voor operationele professionals."
            : "Practical articles on mobile truckwash, compliance, fleet cleaning and sustainability — by and for operational professionals."}
        </p>
      </section>

      {/* PRAKTIJKCASE — handmatig getoonde case-study (geen blog API entry) */}
      <section
        data-testid="blog-praktijkcase-strip"
        className="mx-auto max-w-7xl px-6 lg:px-8 mb-14"
      >
        <Link
          to="/van-demo-tot-digitale-wagenparkcontrole"
          data-testid="blog-praktijkcase-link"
          className="group relative overflow-hidden rounded-2xl border border-[#00D4FF]/20 block card-hover bg-[#0F0F23]"
        >
          <div className="grid grid-cols-1 lg:grid-cols-5">
            <div className="relative aspect-video lg:aspect-auto lg:col-span-2 lg:min-h-[260px]">
              <img
                src="/images/case-demo-after.jpg"
                alt={lang === "nl"
                  ? "Schone laadruimte na mobiele HACCP-reiniging — praktijkcase Wasz.nl"
                  : "Clean cargo area after mobile HACCP cleaning — Wasz.nl case study"}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050509]/50 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#0F0F23]/80" />
            </div>
            <div className="p-7 sm:p-10 lg:col-span-3 flex flex-col justify-center">
              <div className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-[#00D4FF] font-semibold mb-4">
                <span>{lang === "nl" ? "Praktijkcase" : "Case study"}</span>
                <span className="w-1 h-1 rounded-full bg-white/30" />
                <span className="text-white/40">{lang === "nl" ? "Foodtransport" : "Food transport"}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight leading-tight mb-3">
                {lang === "nl"
                  ? "Van Demo tot Digitale Wagenparkcontrole"
                  : "From Demo to Digital Fleet Control"}
              </h2>
              <p className="text-white/60 text-sm sm:text-base leading-relaxed">
                {lang === "nl"
                  ? "Hoe drie demo-voertuigen leidden tot een meerjarige samenwerking — en uiteindelijk tot realtime wagenparkinzicht via WaszBase."
                  : "How a three-vehicle demo grew into a multi-year partnership — and ultimately into real-time fleet visibility through WaszBase."}
              </p>
              <div className="mt-6 inline-flex items-center gap-1.5 text-[#00D4FF] text-sm font-semibold">
                {lang === "nl" ? "Lees praktijkcase" : "Read case study"}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </Link>
      </section>

      {featured && (
        <section className="mx-auto max-w-7xl px-6 lg:px-8 mb-16">
          <Link
            to={featured.url_path || `/blog/${featured.slug}`}
            data-testid="blog-featured"
            className="group relative overflow-hidden rounded-2xl border border-white/10 block card-hover"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative aspect-video lg:aspect-auto lg:min-h-[420px]">
                <img src={featured.image} alt={featured[`title_${lang}`]} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050509]/60 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#050509]/40" />
              </div>
              <div className="bg-[#0F0F23] p-8 sm:p-12 flex flex-col justify-center">
                <div className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-[#00D4FF] font-semibold mb-4">
                  <span>{featured[`category_${lang}`]}</span>
                  <span className="w-1 h-1 rounded-full bg-white/30" />
                  <span className="text-white/40">{lang === "nl" ? "Uitgelicht" : "Featured"}</span>
                </div>
                <h2 className="text-2xl sm:text-4xl font-bold text-white tracking-tight leading-tight mb-4">
                  {featured[`title_${lang}`]}
                </h2>
                <p className="text-white/60 text-sm sm:text-base leading-relaxed">{featured[`excerpt_${lang}`]}</p>
                <div className="mt-6 flex items-center gap-5 text-xs text-white/40">
                  <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {featured.date}</span>
                  <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {featured.read_time} min</span>
                </div>
                <div className="mt-6 inline-flex items-center gap-1.5 text-[#00D4FF] text-sm font-semibold">
                  {lang === "nl" ? "Lees artikel" : "Read article"} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </Link>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((p) => (
            <Link
              key={p.slug}
              to={p.url_path || `/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group relative rounded-2xl border border-white/10 bg-[#0F0F23] overflow-hidden card-hover flex flex-col"
            >
              <div className="aspect-video relative overflow-hidden">
                <img src={p.image} alt={p[`title_${lang}`]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute top-4 left-4 px-2.5 py-1 rounded bg-[#050509]/70 backdrop-blur text-[10px] uppercase tracking-wider font-semibold text-[#00D4FF] border border-[#00D4FF]/30">
                  {p[`category_${lang}`]}
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="text-lg font-bold text-white tracking-tight mb-2 leading-snug line-clamp-2">{p[`title_${lang}`]}</h3>
                <p className="text-white/55 text-sm leading-relaxed line-clamp-3 flex-1">{p[`excerpt_${lang}`]}</p>
                <div className="mt-5 flex items-center justify-between text-xs text-white/40">
                  <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {p.date}</span>
                  <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {p.read_time} min</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Blog;
